import { type ReactElement, useMemo, useEffect, useState } from 'react'
import useDesktop from '@/hooks/useDesktop'
import { H2White, Body2White, Body1White } from '@/components/typography'
import PageCard from '@/components/common/PageCard'
import Link from 'next/link'
import { useRouter } from 'next/router'
import { Box, Grid, IconButton, Button, SvgIcon, Tooltip } from '@mui/material'
import MuiLink from '@mui/material/Link'
import css from './styles.module.css'
import { Add, PersonAdd, Edit, Delete } from '@mui/icons-material'
import EthHashInfo from '@/components/common/EthHashInfo'
import { type Inheritor } from '@/store/setUpInheritorsSlice'
import { useAppDispatch, useAppSelector } from '@/store'
import { selectCurrency } from '@/store/settingsSlice'
import { useVisibleBalances } from '@/hooks/useVisibleBalances'
import { formatCurrency } from '@/utils/formatNumber'
import ModalButton from '@/components/common/ModalButton'
// import useInheritance from '@/hooks/useInheritance'
import TransactionLayout from '@/components/common/TransactionLayout/TransactionLayout'
import QrCodeButton from '@/components/sidebar/QrCodeButton'

import SetInheritanceRuleComponent from '@/components/inheritors/SetInheritanceRuleComponent'
import EditInheritorComponent from '@/components/inheritors/EditInheritorConponent'
import RemoveInheritor from '@/components/inheritors/RemoveInheritorComponent'
import SendProcessing from '@/components/inheritors/SendProcessing'

// import { refreshInheritanceInfo } from '@/store/inheritanceInfoSlice'
import { type InheritanceInfoState } from '@/store/inheritanceInfoSlice'

import PersonSearchRoundedIcon from '@mui/icons-material/PersonSearchRounded'
import PostAddRoundedIcon from '@mui/icons-material/PostAddRounded'
import useAddressBook from '@/hooks/useAddressBookV2'
import Assets from '@/components/dashboard/Assets/Assets'
import RuleRender from '@/components/inheritors/RuleRender'
import useLoadInheritance from '@/hooks/loadables/useLoadInheritance'
import { trackEvent, INHERIT_EVENTS } from '@/services/analytics'

// import { formatDate } from '@/utils/date'

const InheritorsConponent = (): ReactElement => {
  const isDesktop = useDesktop()
  const [refreshCounter, setRefreshCounter] = useState<number>(0)
  const [inheritance, error, loading] = useLoadInheritance(refreshCounter)
  const dispatch = useAppDispatch()
  const addressBook = useAddressBook()
  const [inheritanceInfo, setInheritanceInfo] = useState<InheritanceInfoState | undefined>(inheritance)
  const currency = useAppSelector(selectCurrency)
  const { balances, loading: balancesLoading } = useVisibleBalances()
  const fiatTotal = useMemo(
    () => (balances.fiatTotal ? formatCurrency(balances.fiatTotal, currency) : ''),
    [currency, balances.fiatTotal],
  )
  const numTokens = useMemo(() => balances.items.filter((token) => token.balance !== '0').length, [balances])

  const notes = [
    'Only you have ownership to this account.',
    'You can totally send or grow your assets any time via well known DeFi protocols such as swap, lending, liquid staking, etc.',
    'You can change these rules anytime.',
  ]

  useEffect(() => {
    const timerId = setTimeout(() => setRefreshCounter(refreshCounter + 1), 5e3)
    return () => clearTimeout(timerId)
  }, [refreshCounter, setRefreshCounter])

  useEffect(() => {
    if (!inheritance) return

    const inheritors = inheritance.inheritors.map((inheritor) => {
      return {
        ...inheritor,
        name: addressBook[inheritor.address]?.name || `Inheritor ${inheritor.id}`,
        email: addressBook[inheritor.address]?.email || '',
      }
    })

    setInheritanceInfo({ ...inheritance, inheritors })
  }, [addressBook, inheritance])

  const onSendSuccess = () => {
    // dispatch(refreshInheritanceInfo(false))
    setRefreshCounter(refreshCounter + 1)
  }

  const renderInheritor = (index: number, inheritor: Inheritor) => {
    return (
      <Grid
        key={`inheritor-${index}`}
        container
        px="16px"
        py="8px"
        mb="16px"
        sx={{
          borderRadius: '4px',
          border: '1px solid var(--Ebony-Clay-950, #2F354B)',
          // background: 'var(--Supporting-Dark-BG-700, #272A3E)',
        }}
      >
        <Grid item container xs="auto" sm="auto" alignContent="center" justifyContent="flex-start">
          <Body1White
            sx={{
              width: isDesktop ? '64px' : '54px',
              background: 'var(--Ebony-Clay-950, #2F354B)',
              borderRadius: '4px',
              paddingX: isDesktop ? '16px' : '8px',
              paddingY: '8px',
              color: inheritor.percentage !== 0 ? 'var(--Emerald-600, #22C55E)' : 'var(--Ebony-Clay-700, #485581)',
              textAlign: 'center',
            }}
          >
            {inheritor.percentage !== 0 ? `${inheritor.percentage}%` : '-%-'}
          </Body1White>
        </Grid>
        <Grid item xs={9} sm="auto" pl={isDesktop ? '16px' : '8px'}>
          <Body1White sx={{ lineHeight: '140%', color: 'var(--Ebony-Clay-300, #D2D7E5)' }}>
            {inheritor.name === '' ? `Inheritor ${index + 1}` : inheritor.name}
          </Body1White>
          <Body2White
            sx={{
              lineHeight: '140%',
              color: !inheritor.email ? 'var(--Ebony-Clay-700, #485581)' : 'var(--Ebony-Clay-400, #AAB3CF)',
            }}
          >
            {inheritor.email || '-- Empty email --'}
          </Body2White>
          {inheritor.address !== '' && (
            <EthHashInfo
              address={inheritor.address}
              hasExplorer={false}
              showCopyButton={false}
              showName={false}
              shortAddress={false}
              showAvatar={false}
              shortenLen={8}
              showPrefix={isDesktop ? true : false}
            />
          )}
          {inheritor.address === '' && (
            <Body2White sx={{ lineHeight: '140%', color: 'var(--Ebony-Clay-700, #485581)' }}>
              -- Inheritor address --
            </Body2White>
          )}
        </Grid>
        {inheritor.claimed && (
          <Grid item container xs={12} sm alignContent="center" justifyContent="flex-end" mt="4px">
            <Body1White
              sx={{
                marginTop: '4px',
                border: '1px solid var(--Blue-Ribbon-600, #5D5DFF)',
                borderRadius: '4px',
                color: 'var(--Blue-Ribbon-600, #5D5DFF)',
                fontSize: '12px',
                fontWeight: '400',
                padding: '0 8px',
                // width: isDesktop ? '64px' : '54px',
              }}
            >
              claimed
            </Body1White>
          </Grid>
        )}

        {inheritanceInfo && inheritanceInfo.status !== 2 && (
          <Grid item container xs={12} sm alignContent="center" justifyContent="flex-end" mt="4px">
            <Grid item xs={6} sm="auto">
              <ModalButton
                titleModal={'Edit inheritor'}
                contentModal={
                  <SendProcessing>
                    <EditInheritorComponent
                      index={index}
                      inheritors={inheritanceInfo.inheritors}
                      maxPercentage={
                        100 - inheritanceInfo.allocated / 100 + inheritanceInfo.inheritors[index].percentage
                      }
                      onSendSuccess={onSendSuccess}
                    />
                  </SendProcessing>
                }
              >
                <Tooltip title="Edit the inheritor" placement="bottom">
                  <IconButton
                    onClick={() => trackEvent(INHERIT_EVENTS.CLICK_EDIT_INHERITOR)}
                    sx={{
                      background: 'var(--Supporting-Dark-BG-700, #272A3E)',
                      borderRadius: '4px 0px 0px 4px',
                      marginRight: '2px',
                      color: '#D2D7E5',
                    }}
                  >
                    <Edit fontSize="small" />
                  </IconButton>
                </Tooltip>
              </ModalButton>
            </Grid>
            <Grid item xs={6} sm="auto">
              <ModalButton
                titleModal={'Remove inheritor'}
                contentModal={
                  <SendProcessing>
                    <RemoveInheritor index={index} inheritor={inheritor} onSendSuccess={onSendSuccess} />
                  </SendProcessing>
                }
              >
                <Tooltip title="Remove the inheritor" placement="bottom">
                  <IconButton
                    onClick={() => trackEvent(INHERIT_EVENTS.CLICK_DELETE_INHERITOR)}
                    sx={{
                      background: 'var(--Supporting-Dark-BG-700, #272A3E)',
                      borderRadius: '0px 4px 4px 0px',
                      color: '#D2D7E5',
                    }}
                  >
                    <Delete fontSize="small" />
                  </IconButton>
                </Tooltip>
              </ModalButton>
            </Grid>
          </Grid>
        )}
      </Grid>
    )
  }

  // if (loading && !inheritanceInfo)
  //   return (
  //     <TransactionLayout>
  //       <PageCard title="Inheritance Management">
  //         <div>Loading...</div>
  //       </PageCard>
  //     </TransactionLayout>
  //   )

  if (!inheritanceInfo || inheritanceInfo.rule.type === 0)
    return (
      <TransactionLayout>
        <PageCard title="Inheritance Management">
          <NoInheritance isDesktop={isDesktop} />
        </PageCard>
      </TransactionLayout>
    )

  return (
    <TransactionLayout>
      <PageCard title="Inheritance Management">
        <>
          <Grid container pt="16px" px={isDesktop ? '16px' : '0px'}>
            <Grid item xs={12} sm={8}>
              <Grid
                container
                px="16px"
                py="8px"
                sx={{
                  borderRadius: '4px',
                  border: '1px solid var(--Ebony-Clay-950, #2F354B)',
                }}
              >
                <Grid item xs={12} sm={8}>
                  <Grid item container xs={12}>
                    <Grid item xs="auto">
                      <Body2White sx={{ color: 'var(--Ebony-Clay-500, #7C8AB4)' }}>
                        Total asset value of {numTokens === 0 ? '0 token' : ''}{' '}
                      </Body2White>
                    </Grid>
                    {numTokens > 0 && (
                      <Grid item container xs textAlign="left">
                        <ModalButton titleModal={'Assets'} contentModal={<Assets showTitle={false} isModal={true} />}>
                          <Tooltip title="Show list of tokens" placement="bottom">
                            <MuiLink
                              component="button"
                              sx={{
                                marginLeft: '4px',
                                fontSize: '14px',
                                color: 'var(--Blue-Ribbon-600, #5D5DFF)',
                                textDecorationColor: 'var(--Blue-Ribbon-600, #5D5DFF)',
                                '&:hover': {
                                  color: 'var(--Blue-Ribbon-700, #4536F5)',
                                },
                              }}
                              underline="always"
                            >
                              {numTokens} {numTokens > 1 ? 'tokens' : 'token'}
                            </MuiLink>
                          </Tooltip>
                        </ModalButton>
                      </Grid>
                    )}
                  </Grid>
                  <H2White sx={{ fontSize: '24px' }}>
                    ${fiatTotal.replace('USD', '')}{' '}
                    <span style={{ color: 'var(--Ebony-Clay-500, #7C8AB4)', fontWeight: 300 }}>USD</span>
                  </H2White>
                  <Body2White sx={{ color: 'var(--Ebony-Clay-500, #7C8AB4)' }}>
                    {highlightPurpleText(`${inheritanceInfo.allocated / 100}%`)} allocated to inheritors
                  </Body2White>
                </Grid>
                <Grid item xs={12} sm={4} container flexDirection="row" alignContent="center" justifyContent="flex-end">
                  <QrCodeButton>
                    <Button
                      sx={{
                        border: '1px solid var(--Emerald-600, #22C55E)',
                        color: 'var(--Emerald-600, #22C55E)',
                        fontSize: '14px',
                        fontWeight: 'bold',
                        lineHeight: '160%',
                        paddingY: '8px',
                        paddingX: '16px',
                        width: isDesktop ? 'auto' : '100%',
                        marginBottom: isDesktop ? '0px' : '4px',
                        marginTop: isDesktop ? '0px' : '8px',
                      }}
                      startIcon={<Add fontSize="small" />}
                    >
                      Add funds
                    </Button>
                  </QrCodeButton>
                </Grid>
              </Grid>

              <Grid
                container
                mt="16px"
                mb="16px"
                sx={{
                  borderRadius: '4px',
                  border: '1px solid var(--Ebony-Clay-950, #2F354B)',
                }}
              >
                <>
                  <Grid container px="16px" sx={{ background: 'var(--Supporting-Dark-BG-700, #272A3E)' }}>
                    <Grid item xs={12} sm py={isDesktop ? '16px' : '8px'}>
                      <RuleRender
                        rule={inheritanceInfo.rule}
                        numInheritors={inheritanceInfo.inheritors.length}
                        showTag
                        lastActivity={inheritanceInfo.lastActivity}
                        numClaimed={inheritanceInfo.claimed}
                      />
                    </Grid>
                    {inheritanceInfo.status !== 2 && (
                      <Grid
                        item
                        container
                        xs={12}
                        sm="auto"
                        flexDirection="row"
                        alignContent="center"
                        justifyContent="flex-end"
                        textAlign="right"
                        mb={isDesktop ? '0px' : '8px'}
                      >
                        <Grid item xs={6} sm="auto">
                          <ModalButton
                            titleModal={'Set inheritance rules'}
                            contentModal={
                              <SendProcessing>
                                <SetInheritanceRuleComponent
                                  rule={inheritanceInfo.rule}
                                  onSendSuccess={onSendSuccess}
                                />
                              </SendProcessing>
                            }
                          >
                            <Tooltip title="Edit the inheritance rule" placement="bottom">
                              <IconButton
                                onClick={() => trackEvent(INHERIT_EVENTS.CLICK_EDIT_RULE)}
                                sx={{
                                  background: 'var(--Ebony-Clay-950, #2F354B)',
                                  borderRadius: '4px 0px 0px 4px',
                                  marginRight: '2px',
                                  color: '#D2D7E5',
                                }}
                              >
                                <Edit fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </ModalButton>
                        </Grid>

                        {inheritanceInfo.allocated < 10_000 && (
                          <Grid item xs={6} sm="auto">
                            <ModalButton
                              titleModal={'Add new inheritor'}
                              contentModal={
                                <SendProcessing>
                                  <EditInheritorComponent
                                    index={-1}
                                    maxPercentage={100 - inheritanceInfo.allocated / 100}
                                    inheritors={inheritanceInfo.inheritors}
                                    onSendSuccess={onSendSuccess}
                                  />
                                </SendProcessing>
                              }
                            >
                              <Tooltip title="Add a new inheritor" placement="bottom">
                                <IconButton
                                  onClick={() => trackEvent(INHERIT_EVENTS.CLICK_ADD_INHERITOR)}
                                  sx={{
                                    background: 'var(--Ebony-Clay-950, #2F354B)',
                                    borderRadius: '0px 4px 4px 0px',
                                    color: '#D2D7E5',
                                  }}
                                >
                                  <PersonAdd fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </ModalButton>
                          </Grid>
                        )}
                      </Grid>
                    )}
                  </Grid>
                  <Grid
                    item
                    container
                    xs={12}
                    px="16px"
                    pt="16px"
                    alignContent="flex-end"
                    justifyContent="flex-end"
                  ></Grid>
                  <Box
                    className={css.confirmInheritanceScroll}
                    sx={{ width: '100%', paddingX: '16px', paddingBottom: '8px' }}
                  >
                    {inheritanceInfo &&
                      inheritanceInfo.inheritors.map((inheritor, index) => renderInheritor(index, inheritor))}
                  </Box>
                </>
              </Grid>
            </Grid>
            {isDesktop && (
              <Grid item xs={12} sm={4} pl="32px">
                <Box
                  // mt="16px"
                  p="16px"
                  sx={{
                    borderRadius: '4px',
                    border: '1px solid var(--Ebony-Clay-950, #2F354B)',
                  }}
                >
                  <Body2White sx={{ color: 'var(--Supporting-Koromiko, #FC8313)' }}>
                    Please note before the rules are met:
                  </Body2White>
                  {notes.map((note, index) => {
                    return (
                      <Grid key={`note-${index}`} container mt="8px">
                        <Grid item xs="auto">
                          <SvgIcon viewBox="0 0 12 12" sx={{ width: '12px', height: '12px', mr: '8px' }}>
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="12"
                              height="12"
                              viewBox="0 0 12 12"
                              fill="none"
                            >
                              <g clipPath="url(#clip0_2805_1002)">
                                <path
                                  d="M6 1C3.235 1 1 3.235 1 6C1 8.765 3.235 11 6 11C8.765 11 11 8.765 11 6C11 3.235 8.765 1 6 1Z"
                                  fill="#FC8313"
                                />
                              </g>
                              <defs>
                                <clipPath id="clip0_2805_1002">
                                  <rect width="12" height="12" fill="white" />
                                </clipPath>
                              </defs>
                            </svg>
                          </SvgIcon>
                        </Grid>
                        <Grid item xs>
                          <Body2White>{note}</Body2White>
                        </Grid>
                      </Grid>
                    )
                  })}
                </Box>
              </Grid>
            )}
          </Grid>
        </>
      </PageCard>
    </TransactionLayout>
  )
}

const NoInheritance = ({ isDesktop }: { isDesktop: boolean }): ReactElement => {
  const router = useRouter()

  return (
    <Grid container pt="16px" px={isDesktop ? '16px' : '0px'} py="16px" justifyContent="center">
      <Grid
        item
        xs={12}
        textAlign="center"
        sx={{
          borderRadius: '4px',
          border: '1px solid var(--Ebony-Clay-950, #2F354B)',
        }}
        px="16px"
        py="32px"
      >
        <PersonSearchRoundedIcon sx={{ fontSize: '64px', color: 'var(--Ebony-Clay-700, #485581)' }} />
        <Body2White sx={{ color: 'var(--Ebony-Clay-600, #5B6B9C)', fontSize: '16px' }}>No inheritors yet!</Body2White>
        <Body1White sx={{ color: 'var(--Ebony-Clay-300, #D2D7E5)', fontSize: '16px', mt: '8px' }}>
          Let&apos;t set up your inheritance now!
        </Body1White>
        <Link
          href={{
            pathname: '/set-up-inheritance',
            query: { wallet: router.query.wallet },
          }}
          passHref
        >
          <Button
            sx={{
              border: '1px solid var(--Emerald-600, #22C55E)',
              color: 'var(--Emerald-600, #22C55E)',
              fontSize: '14px',
              fontWeight: 'bold',
              lineHeight: '180%',
              paddingY: '8px',
              paddingX: '16px',
              width: isDesktop ? 'auto' : '100%',
              minWidth: '280px',
              marginBottom: '4px',
              marginTop: '16px',
              '&> span > *:first-child': {
                fontSize: '22px',
              },
            }}
            startIcon={<PostAddRoundedIcon fontSize="large" />}
          >
            Set up inheritance
          </Button>
        </Link>
      </Grid>
    </Grid>
  )
}

const highlightPurpleText = (text: string) => {
  return <span style={{ color: 'var(--Blue-Ribbon-600, #5D5DFF)', fontWeight: 'bold' }}>{text}</span>
}

export default InheritorsConponent
